import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { forTablet, forDesktop, forIE } from '@westwing/ui-kit/breakPoints';
import { caption1 } from '@westwing/ui-kit/typography';
import HybridLink from '../../Generic/Links/HybridLink';
import { SeoListItem } from '../../Generic/SeoHead/SeoBreadcrumbScript';

interface BreadCrumbsProps {
    breadcrumbList: SeoListItem[];
}

export const StyledBreadCrumbAnchor = styled(HybridLink)`
    text-decoration: none;
    color: ${({ theme }) => theme.colors.charcoal100};
    ${caption1}
`;

export const StyledBreadCrumbOrderedList = styled.ol`
    list-style: none;
    padding: 9px 14px 0;
    text-align: left;
    white-space: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        width: 0 !important;
    }
    ${forTablet`
        padding: 9px 0;
    `}
    ${forDesktop`
        overflow-x: visible;
    `}
    ${forIE`
        margin: 0;
    `}
`;

export const StyledBreadCrumbListItem = styled.li`
    display: inline;
    + li::before {
        display: inline-block;
        color: ${({ theme }) => theme.colors.charcoal100};
        content: '/';
        margin: 0 0.3em;
    }
`;

interface AnchorProps {
    to: string;
    textType: string;
    isCurrentPage?: boolean;
}

const breadCrumbListItemGenerator = (count: number) => (breadCrumbItem: SeoListItem, i: number) => {
    const { name, item } = breadCrumbItem;
    const id: string = `${name}--${item}`;

    const anchorProps: AnchorProps = {
        to: item,
        textType: 'caption',
    };

    // the last link should have current page aria property
    if (count - 1 === i) {
        anchorProps.isCurrentPage = true;
    }

    // the home link at beginning of bread crumbs should be external so it goes to alice
    if (i === 0) {
        anchorProps.to = '/';
    }

    return (
        <StyledBreadCrumbListItem key={id} data-testid="breadcrumb-list-item">
            <StyledBreadCrumbAnchor {...anchorProps}>{name}</StyledBreadCrumbAnchor>
        </StyledBreadCrumbListItem>
    );
};

const BreadCrumbs: React.FC<BreadCrumbsProps> = ({ breadcrumbList }) => {
    const linksWithSeparatorLines: ReactElement[] = breadcrumbList.map(
        breadCrumbListItemGenerator(breadcrumbList.length)
    );

    return (
        <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav">
            <StyledBreadCrumbOrderedList>{linksWithSeparatorLines}</StyledBreadCrumbOrderedList>
        </nav>
    );
};

export default BreadCrumbs;
